import * as React from 'react';
import './ExpenseForm.scss';
import { TK } from '../../../store/translations/translationKeys';
import { Spinner, Form, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useTranslations } from '../../../store/translations/hooks';
import Delete from '@material-ui/icons/Delete';
import { TextInputField, NumberInputField, SwitchField, SelectTextField, AutocompleteTextField, ExpenseAttachmentsField, DateField } from '../fields';
import { ExpenseData, RecurringUnit } from '../../../models';
import ChangeLogInfo from '../../ChangeLogInfo';
import { ChangeLog } from '../../../models';
import { useMemo } from 'react';
import { useGeneralSettings } from '../../../store/settings/hooks';
import { updateGeneralSettings } from '../../../store/settings/actions';
import { useDispatch } from 'react-redux';
import { Row } from '../table/Row';
import { Col } from '../table/Column';
import { CheckInputField } from '../fields/CheckInputField';
import AppButton from '../../AppButton';

export interface ExpenseFormStateProps {
    data: ExpenseData,
    hasChanged: boolean,
    isSubmitting?: boolean,
    type: 'edit' | 'create',
    canEdit: boolean,
    noRecurring?: boolean,
    changeLog?: ChangeLog[],
}

export interface ExpenseFormDispatchProps {
    onChange: (data: ExpenseData) => void,
    onLoad?: () => void,
    onSave: (data: ExpenseData, files?: File[]) => void,
    onCancel?: () => void,
    onDelete?: () => void,
}

export type ExpenseFormProps = ExpenseFormStateProps & ExpenseFormDispatchProps;

const ExpenseForm: React.FC<ExpenseFormProps> = ({
    type,
    data,
    hasChanged,
    isSubmitting,
    noRecurring,
    onChange,
    onLoad,
    onSave,
    onCancel,
    onDelete,
    canEdit,
    changeLog,
}) => {

    const t = useTranslations();
    const dispatch = useDispatch();
    const generalSettings = useGeneralSettings();
    const {paymentMethods, expenseTypes, expenseEntities} = generalSettings;

    const [deleteModal, setDeleteModal] = React.useState(false);
    const [newFiles, setNewFiles] = React.useState<File[]>([]);
    const [errors, setErrors] = React.useState<{ name?: string; date?: string; type?: string; entity?: string; paymentType?: string }>({});
    const [unitPrices, setUnitPrices] = React.useState<{withVAT: number, withoutVAT: number}>({
        withoutVAT: 0,
        withVAT: 0,
    })

    // eslint-disable-next-line
    React.useEffect(() => { onLoad && onLoad(); }, []);

    const isEditing = type === 'edit';

    const id = useMemo(() => isEditing ? data.id : 'new', [isEditing, data.id]);

    const isRecurring = !!data.recurring;

    const recurringUnitOptions = useMemo(() => [
        { value: RecurringUnit.Days, label: t(TK.days) },
        { value: RecurringUnit.Weeks, label: t(TK.weeks) },
        { value: RecurringUnit.Months, label: t(TK.months) },
    ], [t]);

    const handleSave = () => {
        const newErrors: { name?: string; date?: string; type?: string; entity?: string; paymentType?: string } = {};
        if (!data.name?.trim()) newErrors.name = t(TK.thisFieldIsMandatory);
        if (!data.type?.trim()) newErrors.type = t(TK.thisFieldIsMandatory);
        if (!data.entity?.trim()) newErrors.entity = t(TK.thisFieldIsMandatory);
        if (!data.payment?.date) newErrors.date = t(TK.thisFieldIsMandatory);
        if (!data.payment?.type?.trim()) newErrors.paymentType = t(TK.thisFieldIsMandatory);

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setErrors({});
        onSave({
            ...data,
            name: data.name?.trim(),
        }, newFiles);
    };

    React.useEffect(() => {
        const withVAT = data.payment.value / (data.payment.amount ?? 1)
        const withoutVAT = withVAT - (withVAT * (data.payment.vat ?? 0))
        setUnitPrices({withoutVAT, withVAT})
    }, [data.payment.vat])

    const updateAmount = (amount?: number) => {
        onChange({
            ...data,
            payment: {
                ...data.payment,
                amount: amount ?? 1,
                value: (data.payment.value / (data.payment.amount ?? 1)) * (amount ?? 1),
            },
        })
    }

    const updateUnitPriceWithVAT = (withVAT?: number) => {
        if (!withVAT) return

        setUnitPrices({
            withVAT,
            withoutVAT: withVAT/(1+(data.payment.vat ?? 0)),
        })

        onChange({
            ...data,
            payment: {
                ...data.payment,
                value: (withVAT ?? 0) * (data.payment.amount ?? 1),
            },
        })
    }

    const updateUnitPriceWithoutVAT = (withoutVAT?: number) => {
        if (!withoutVAT) return

        setUnitPrices({
            withoutVAT,
            withVAT: withoutVAT*(1+(data.payment.vat ?? 0)),
        })

        onChange({
            ...data,
            payment: {
                ...data.payment,
                value: (withoutVAT ?? 0) * (1 + (data.payment.vat ?? 0)) * (data.payment.amount ?? 1),
            },
        })
    }

    const updateTotalPriceWithoutVAT = (totalWithoutVAT?: number) => {
        if (!totalWithoutVAT) return

        setUnitPrices({
            withoutVAT: totalWithoutVAT / (data.payment.amount ?? 1),
            withVAT: (totalWithoutVAT / (data.payment.amount ?? 1)) * (1 + (data.payment.vat ?? 0)),
        })

        onChange({
            ...data,
            payment: {
                ...data.payment,
                value: (totalWithoutVAT ?? 0) * (1 + (data.payment.vat ?? 0)),
            },
        })
    }

    const updateTotalPriceWithVAT = (totalWithVAT?: number) => {
        if (!totalWithVAT) return

        setUnitPrices({
            withoutVAT: (totalWithVAT / (data.payment.amount ?? 1)) / (1 + (data.payment.vat ?? 0)),
            withVAT: totalWithVAT / (data.payment.amount ?? 1),
        })

        onChange({
            ...data,
            payment: {
                ...data.payment,
                value: totalWithVAT,
            },
        })
    }

    return (
        <Form style={{display: 'flex', marginTop: 'var(--spacer-S)', flexDirection: 'column', gap: 'var(--spacer-M)'}}>
            <TextInputField
                id={`expense-name-${id}`}
                label={t(TK.name)}
                value={data.name || ''}
                disabled={!canEdit}
                invalid={!!errors.name}
                description={errors.name}
                onChange={name => {
                    if (errors.name) setErrors(prev => ({ ...prev, name: undefined }));
                    onChange({...data, name});
                }}
            />

            <AutocompleteTextField
                id={`expense-type-${id}`}
                label={t(TK.type)}
                options={expenseTypes}
                value={data.type || ''}
                disabled={!canEdit}
                invalid={!!errors.type}
                description={errors.type}
                onValueChanged={type => {
                    if (errors.type) setErrors(prev => ({ ...prev, type: undefined }));
                    onChange({...data, type});
                }}
                onAddNewOption={newType => dispatch(updateGeneralSettings({
                    ...generalSettings,
                    expenseTypes: [...generalSettings.expenseTypes, newType],
                }))}
            />

            <AutocompleteTextField
                id={`expense-entity-${id}`}
                label={t(TK.entity)}
                options={expenseEntities}
                value={data.entity || ''}
                disabled={!canEdit}
                invalid={!!errors.entity}
                description={errors.entity}
                onValueChanged={entity => {
                    if (errors.entity) setErrors(prev => ({ ...prev, entity: undefined }));
                    onChange({...data, entity});
                }}
                onAddNewOption={newEntity => dispatch(updateGeneralSettings({
                    ...generalSettings,
                    expenseEntities: [...generalSettings.expenseEntities, newEntity],
                }))}
            />

            <TextInputField
                id={`expense-notes-${id}`}
                label={t(TK.notes)}
                type='textarea'
                value={data.notes || ''}
                disabled={!canEdit}
                onChange={notes => onChange({...data, notes})}
            />

            <Row>
                <Col md={4}>
                    <DateField
                        id={`expense-payment-date-${id}`}
                        label={t(TK.paymentDate)}
                        value={data.payment?.date || ''}
                        type="date"
                        disabled={!canEdit}
                        invalid={!!errors.date}
                        description={errors.date}
                        onChange={date => {
                            if (errors.date) setErrors(prev => ({ ...prev, date: undefined }));
                            onChange({
                                ...data,
                                payment: {...data.payment, date: date.substring(0, 10)}
                            });
                        }}
                    />
                </Col>
                <Col md={4}>
                    <AutocompleteTextField
                        id={`expense-payment-type-${id}`}
                        label={t(TK.paymentType)}
                        value={data.payment?.type || ''}
                        disabled={!canEdit}
                        options={paymentMethods}
                        invalid={!!errors.paymentType}
                        description={errors.paymentType}
                        onValueChanged={type => {
                            if (errors.paymentType) setErrors(prev => ({ ...prev, paymentType: undefined }));
                            onChange({
                                ...data,
                                payment: {...data.payment, type}
                            });
                        }}
                    />
                </Col>
                <Col md={4}>
                    <NumberInputField
                        id={`expense-payment-vat-${id}`}
                        label={t(TK.vat)}
                        append="%"
                        value={(data.payment?.vat ?? 0)*100}
                        disabled={!canEdit}
                        min={0}
                        max={100}
                        step={1}
                        onChange={vat => onChange({
                            ...data,
                            payment: {...data.payment, vat: (vat ?? 0)/100}
                        })}
                    />
                </Col>
            </Row>

            <Row>
                <Col md={4}>
                    <NumberInputField
                        id={`expense-quantity-${id}`}
                        label={t(TK.quantity)}
                        value={data.payment?.amount ?? 1}
                        disabled={!canEdit}
                        onChange={updateAmount}
                    />
                </Col>
                <Col md={4}>
                    <NumberInputField
                        id={`expense-unit-price-without-vat`}
                        label={t(TK.unitPrice)}
                        prepend="€"
                        value={unitPrices.withoutVAT}
                        disabled={!canEdit}
                        onChange={updateUnitPriceWithoutVAT}
                    />
                </Col>
                <Col md={4}>
                    <NumberInputField
                        id={`expense-total-without-vat`}
                        label={t(TK.total)}
                        prepend="€"
                        value={data.payment.value / (1+(data.payment.vat ?? 0))}
                        disabled={!canEdit}
                        onChange={updateTotalPriceWithoutVAT}
                    />
                </Col>
            </Row>

            {!!data.payment.vat && 
                <Row>
                    <Col md={4} />
                    <Col md={4}>
                        <NumberInputField
                            id={`expense-unit-price-with-vat`}
                            label={`${t(TK.unitPrice)} (${t(TK.withVAT)})`}
                            prepend="€"
                            value={unitPrices.withVAT}
                            disabled={!canEdit}
                            onChange={updateUnitPriceWithVAT}
                        />
                    </Col>
                    <Col md={4}>
                        <NumberInputField
                            id={`expense-total-with-vat`}
                            label={`${t(TK.total)} (${t(TK.withVAT)})`}
                            prepend="€"
                            value={data.payment.value}
                            disabled={!canEdit}
                            onChange={updateTotalPriceWithVAT}
                        />
                    </Col>
                </Row>
            }
            
            <ExpenseAttachmentsField
                id={`expense-attachments-${id}`}
                label={t(TK.attachments)}
                disabled={!canEdit}
                existingAttachments={data.attachments || []}
                newFiles={newFiles}
                onExistingAttachmentsChange={attachments => onChange({...data, attachments})}
                onNewFilesChange={setNewFiles}
            />

            {!noRecurring && <>
                <hr style={{width: '100%'}}/>

                <Row>
                    <Col md={3}>
                        <CheckInputField
                            id={`expense-recurring-switch-${id}`}
                            label={t(TK.recurring)}
                            disabled={!canEdit}
                            value={isRecurring}
                            onChange={() => onChange(isRecurring
                                ? {...data, recurring: undefined}
                                : {...data, recurring: { unit: RecurringUnit.Months, value: 1 }})}
                        />
                    </Col>

                    { isRecurring && data.recurring && <>
                        <Col md={2} style={{alignContent: 'end'}}>
                            <NumberInputField
                                id={`expense-recurring-value-${id}`}
                                value={data.recurring.value ?? 1}
                                disabled={!canEdit}
                                onChange={value => onChange({
                                    ...data,
                                    recurring: {...data.recurring!, value: value ?? 1}
                                })}
                            />
                        </Col>
                        <Col md={3} style={{alignContent: 'end'}}>
                            <SelectTextField
                                id={`expense-recurring-unit-${id}`}
                                value={recurringUnitOptions.find(o => o.value === data.recurring?.unit)!.label}
                                disabled={!canEdit}
                                options={recurringUnitOptions.map(o => o.label)}
                                onValueChanged={unitLabel => onChange({
                                    ...data,
                                    recurring: {...data.recurring!, unit: recurringUnitOptions.find(o => o.label === unitLabel)!.value}
                                })}
                            />
                        </Col>
                    </>}

                </Row>

                { isRecurring && <Row>
                    <Col md={3}>
                        <CheckInputField
                            id={`expense-recurring-no-end-date-switch-${id}`}
                            label={t(TK.endDate)}
                            disabled={!canEdit}
                            value={!!data.recurring?.endDate}
                            onChange={value => {
                                const lastDayOfYear = new Date(new Date().getFullYear(), 11, 31).toISOString().slice(0, 10);
                                onChange(value
                                    ? { ...data, recurring: { ...data.recurring!, endDate: lastDayOfYear } }
                                    : { ...data, recurring: { ...data.recurring!, endDate: undefined } }
                                );
                            }}
                        />
                    </Col>
                    { !!data.recurring?.endDate &&
                        <Col md={5}>
                            <DateField
                                id={`expense-recurring-end-date-${id}`}
                                value={data.recurring.endDate || ''}
                                type="date"
                                disabled={!canEdit}
                                onChange={endDate => onChange({
                                    ...data,
                                    recurring: {...data.recurring!, endDate: endDate.substring(0, 10)}
                                })}
                            />
                        </Col>
                    }
                </Row>}
            </>}

            <div className="buttonsContainer" >
                { isEditing && canEdit && onDelete && <AppButton id={`expense-delete-button-${id}`} outline color="danger" onClick={() => setDeleteModal(true)}><Delete /></AppButton>}
                { onCancel && (<AppButton id={`expense-cancel-button-${id}`} onClick={onCancel}>{t(TK.cancel)}</AppButton>) }
                <AppButton
                    id={`expense-save-button-${id}`}
                    color="success"
                    disabled={isSubmitting || (!hasChanged && newFiles.length === 0)}
                    onClick={handleSave}
                >
                    {isSubmitting && <Spinner size="sm" color="secondary"/>}
                    {t(isEditing ? TK.save : TK.createExpense)}
                </AppButton>
                { !!changeLog?.length && <ChangeLogInfo changeLog={changeLog}/> }
            </div>

            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)}>
                <ModalHeader toggle={() => setDeleteModal(!deleteModal)}>{t(TK.areYouSure)}</ModalHeader>
                <ModalBody>{t(TK.youRAboutToDelete)}</ModalBody>
                <ModalFooter>
                    <AppButton id={`expense-confirm-delete-button-${id}`} color="danger" onClick={() => { onDelete && onDelete(); setDeleteModal(false); }}>{t(TK.delete)}</AppButton>{' '}
                    <AppButton id={`expense-abort-delete-button-${id}`} color="secondary" onClick={() => setDeleteModal(false)}>{t(TK.cancel)}</AppButton>
                </ModalFooter>
            </Modal>
        </Form>
    );
}

export default ExpenseForm;
